import { Controller, useFormContext } from "react-hook-form";

import { Select } from "ui/select";
import { IconButton } from "ui/icon-button";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";
import { useGoogleFonts } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/google-fonts/use-google-fonts";

import { GoogleFontsInputControllerProps } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/google-fonts/google-fonts.types";

import { GoogleFontOption } from "containers/admin/clients/client/theme-page/preview/preview.types";

import Icons from "assets";

export const GoogleFontInputController = ({
  fontIndex,
  removeFontFromForm,
  fieldIsNotRemovable,
}: GoogleFontsInputControllerProps) => {
  const {
    googleFontsSubmitError,
    updateGoogleFontsList,
    removeGoogleFont,
    // handle events
    handleGoogleFontClear,
    handleGoogleFontChange,
    handleGoogleFontSubmit,
  } = useGoogleFonts();

  const { control, setValue } = useFormContext();
  const { fonts, isEditMode, googleFontsList, googleFontsFetchError } = useThemePreviewContext();

  //render helpers
  const getSelectLabel = (fontNotApplied: boolean) => (
    <div className="flex items-center">
      <span className="mr-1">Google font</span>

      {fontNotApplied && (
        <button
          type="button"
          aria-label="Remove not applied font"
          onClick={() => {
            removeFont(fontIndex);
          }}>
          <Icons.Trash customClass="size-4 text-gray-300" />
        </button>
      )}
    </div>
  );

  const getFontSelectValue = (field: any, options: any) => {
    const currentValue = options.find((option: any) => option?.value === field.value) || null;

    if (currentValue) {
      return {
        value: field.value,
        label: (
          <div id="text" className="truncate max-w-[16ch]">
            <span className="text-ellipsis">{field.label}</span>
          </div>
        ),
      };
    } else {
      return null;
    }
  };

  const getFontButton = (
    isRemoveOrClearAllowed: boolean,
    isApplyingDisabled: boolean,
    value: any,
  ) =>
    isRemoveOrClearAllowed ? (
      <IconButton
        aria-label="Clear font"
        variant="contained"
        size="large"
        disabled={!isEditMode}
        onClick={() => {
          fieldIsNotRemovable ? clearFont(fontIndex) : removeFont(fontIndex);
        }}>
        <Icons.Trash />
      </IconButton>
    ) : (
      <IconButton
        aria-label="Apply font"
        size="large"
        disabled={isApplyingDisabled}
        onClick={(e: any) => handleGoogleFontSubmit(e, value, fontIndex)}>
        <Icons.Checkmark />
      </IconButton>
    );

  //conditional checks
  const checkIfCanBeRemoved = (formValue: string) => {
    return (
      //can be removed if
      //value is NOT empty
      !!formValue &&
      //AND value is applied to Review area
      fonts?.googleFonts?.[fontIndex]?.appliedToPreview
    );
  };

  const checkIfRemoveIconDisplayed = (formValue: string) => {
    return (
      //display if
      //it's the only one font
      !fieldIsNotRemovable &&
      //AND value is NOT applied yet to Review area
      (!formValue || !fonts?.googleFonts?.[fontIndex]?.appliedToPreview)
    );
  };

  const checkIfCheckmarkIconDisabled = (formValue: string) => {
    return (
      //disable if
      //it's readonly mode
      !isEditMode ||
      //OR value is empty
      !formValue ||
      //OR it's already applied to Preview area
      fonts?.googleFonts?.[fontIndex]?.appliedToPreview
    );
  };

  //handle removing
  const removeFont = (fontIndex: number) => {
    const font = fonts?.googleFonts?.[fontIndex];
    const fontValue = typeof font !== "string" ? font?.value : "";
    removeFontFromForm(fontIndex);
    removeGoogleFont(fontValue as string);
    updateGoogleFontsList(fontValue as string);
  };

  //handle clearing
  const clearFont = (fontIndex: number) => {
    const font = fonts?.googleFonts?.[fontIndex];
    const fontValue = typeof font !== "string" ? font?.value : "";
    // change to set value so it does not mess with default values
    setValue(`fonts.googleFonts.${fontIndex}`, null);
    removeGoogleFont(fontValue as string);
    handleGoogleFontClear(fontValue as string);
  };

  return (
    <Controller
      name={`fonts.googleFonts.${fontIndex}`}
      control={control}
      render={({ field }) => {
        const formValue = field.value?.value;

        const isRemoveOrClearAllowed = checkIfCanBeRemoved(formValue as string);
        const fontNotApplied = checkIfRemoveIconDisplayed(formValue as string);
        const isApplyingDisabled = checkIfCheckmarkIconDisabled(formValue as string);

        const options = googleFontsList?.map(({ family }: { family: string }) => ({
          value: family,
          label: family,
          appliedToPreview: false,
        }));

        return (
          <div className="flex">
            <Select
              ariaLabel="Select Google font"
              label={getSelectLabel(fontNotApplied)}
              disabled={!isEditMode}
              selectName={`google-font-${fontIndex}`}
              disableClearable
              errMessage={googleFontsFetchError || googleFontsSubmitError}
              options={options || []}
              placeholder="Google font..."
              {...field}
              onChange={(font) => {
                const oldFont = field.value?.value;
                field.onChange(font);
                if (font) {
                  handleGoogleFontChange(fontIndex, font, oldFont as string);
                }
              }}
              // value={options.find((option) => option?.value === formValue) || null}
              value={getFontSelectValue(field.value, options)}
              actionTrigger={getFontButton(
                isRemoveOrClearAllowed as boolean,
                isApplyingDisabled as boolean,
                field.value,
              )}
            />
          </div>
        );
      }}
    />
  );
};
